import { UserButton } from '@clerk/react'
import { Badge, Sidebar, Text } from '@cloudflare/kumo'
import { useCallback, useEffect, useState, type MouseEvent } from 'react'
import { useLocation, useNavigate } from 'react-router'
import type { AdminSummary } from '@repo/shared'
import { adminApi } from './api'
import { env } from './env'
import { useGetToken } from './lib/auth'
import { onSummaryRefresh } from './lib/summary'
import { NAV } from './nav'
import { AppRoutes } from './routes'
import { useManufacturingFeature } from './lib/manufacturing-api'
import { pageVisible, useAdminAccess } from './lib/admin-access'

const OPEN_KEY = 'admin:sidebar:open'
const SUMMARY_POLL_MS = 60_000

function readOpen(): boolean {
  try {
    return localStorage.getItem(OPEN_KEY) !== '0'
  } catch {
    return true
  }
}

/** 사이드바(접이식, 아이콘 모드) + 페이지 영역. 열림 상태는 localStorage 에 기억. 견적 미처리 건수는 1분마다 갱신 */
export function Shell() {
  const manufacturingVisible = useManufacturingFeature()
  const access = useAdminAccess()
  const [open, setOpen] = useState(readOpen)
  const location = useLocation()
  const navigate = useNavigate()
  const getToken = useGetToken()
  const [summary, setSummary] = useState<AdminSummary | null>(null)

  const onOpenChange = useCallback((next: boolean) => {
    setOpen(next)
    try {
      localStorage.setItem(OPEN_KEY, next ? '1' : '0')
    } catch {
      // 무시
    }
  }, [])

  useEffect(() => {
    let alive = true
    const load = () =>
      adminApi
        .summary(getToken)
        .then((s) => alive && setSummary(s))
        .catch(() => undefined)
    void load()
    const t = setInterval(() => void load(), SUMMARY_POLL_MS)
    const off = onSummaryRefresh(() => void load())
    return () => {
      alive = false
      clearInterval(t)
      off()
    }
    // 화면을 옮길 때마다 새로 읽는다 (견적을 처리하고 나오면 배지가 바로 줄어야 한다)
  }, [getToken, location.pathname])

  // 사이드바 링크는 href 로 렌더되므로 전체 새로고침 대신 클라이언트 라우팅으로 가로챈다
  const interceptLinks = (e: MouseEvent<HTMLElement>) => {
    const a = (e.target as HTMLElement).closest('a[href^="/"]')
    if (!a || e.metaKey || e.ctrlKey) return
    e.preventDefault()
    navigate(a.getAttribute('href') ?? '/')
  }

  const pendingQuotes = summary ? summary.quotes.submitted + summary.quotes.accepted : 0

  // Kumo 사이드바는 h-full 이라 부모가 고정 높이(h-svh)여야 창 전체를 채운다. min-h 로는 내용 높이만큼만 잡힘
  return (
    <Sidebar.Provider open={open} onOpenChange={onOpenChange} collapsible="icon" className="h-svh">
      <Sidebar className="h-full">
        <Sidebar.Header>
          <div className="flex items-center gap-2 px-1">
            <span className="flex size-7 items-center justify-center rounded-md bg-[#F04452] text-xs font-bold text-white">
              R
            </span>
            <Text variant="heading">RecipeCat</Text>
            <Badge variant={env.ENV === 'production' ? 'error' : 'primary'}>{env.ENV}</Badge>
          </div>
        </Sidebar.Header>
        <Sidebar.Content>
          <div onClickCapture={interceptLinks}>
            {NAV.map((group) => ({
              ...group,
              items: group.items.filter(
                (item) =>
                  (item.path !== '/manufacturing' || manufacturingVisible === true) &&
                  pageVisible(item.path, access),
              ),
            }))
              .filter((group) => group.items.length > 0)
              .map((group) => (
                <Sidebar.Group key={group.label}>
                  <Sidebar.GroupLabel>{group.label}</Sidebar.GroupLabel>
                  <Sidebar.Menu>
                    {group.items.map((item) => (
                      <Sidebar.MenuButton
                        key={item.path}
                        icon={item.icon}
                        href={item.path}
                        tooltip={item.label}
                        active={location.pathname === item.path}
                      >
                        {item.label}
                        {item.badge === 'quotes' && pendingQuotes > 0 && (
                          <Sidebar.MenuBadge data-testid="quotes-badge">
                            {pendingQuotes}
                          </Sidebar.MenuBadge>
                        )}
                      </Sidebar.MenuButton>
                    ))}
                  </Sidebar.Menu>
                </Sidebar.Group>
              ))}
          </div>
        </Sidebar.Content>
        <Sidebar.Footer>
          <div className="flex items-center justify-between px-1">
            {env.E2E_TOKEN ? <Badge variant="warning">E2E</Badge> : <UserButton />}
            <Sidebar.Trigger />
          </div>
        </Sidebar.Footer>
        <Sidebar.Rail />
      </Sidebar>

      <main className="bg-kumo-base text-kumo-default h-full min-w-0 flex-1 overflow-y-auto">
        {/* 견적 목록은 열이 많아 화면 폭을 전부 쓴다. 나머지 화면은 읽기 좋은 폭으로 제한 */}
        <div
          className={`mx-auto w-full px-6 py-8 md:px-8 ${location.pathname === '/quotes' ? '' : 'max-w-6xl'}`}
        >
          <AppRoutes />
        </div>
      </main>
    </Sidebar.Provider>
  )
}
